import { api } from './client';
import { nombreLegible } from '@/lib/nombre-legible';

/**
 * La cartera de Richard (tanda 5, Ola A): todos sus contactos con su ficha, para la
 * pantalla «Clientes». Prefijo /api/cartera, del módulo `contactos`.
 *
 * Tipos copiados de `apps/api/src/services/cartera.service.ts` (la web no importa de la API).
 */
export interface FilaDeCartera {
  contactId: string;
  /** name || pushName || teléfono. */
  nombre: string;
  telefono: string | null;
  email: string | null;
  /** 'MM-DD'. */
  cumpleanos: string | null;
  tipo: string | null;
  etapa: string | null;
  pais: string | null;
  nacionalidad: string | null;
  origen: 'chat' | 'importado' | 'anuncio';
  /** T14: de qué base salió («Excel de Richard», «Chat directo», el nombre del anuncio…). */
  fuente: string;
  /** contact.createdAt, ISO. */
  entroEl: string;
  conversacion: null | {
    id: string;
    temperatura: string | null;
    motivo: string | null;
    ultimoMensajeEl: string | null;
    agenteId: string | null;
    personaId: string | null;
    estado: string;
  };
  proximaAccion: null | { descripcion: string; fecha: string | null };
  /** optedOutAt, ISO. */
  bajaEl: string | null;
  /** «Es personal» (familia, amigos): el agente no le escribe. */
  personal: boolean;
  /** Las notas internas en una línea, hasta 200 caracteres (para la búsqueda). */
  notas: string | null;
  /** Tanda 6: el valor de cada campo editable configurado, solo los que tienen texto. */
  ficha: Record<string, string>;
  /** Cuándo se puso a mano la etapa actual, ISO. */
  etapaDesde: string | null;
  /** El Zoom programado más cercano (si ya pasaron todos, el más reciente), ISO. */
  zoomEl: string | null;
  avatarUrl?: string | null;
}

export interface Cartera {
  filas: FilaDeCartera[];
  etapas: string[];
}

const conNombreLegible = (f: FilaDeCartera): FilaDeCartera => ({ ...f, nombre: nombreLegible(f.nombre) });

export const listarCartera = async (): Promise<Cartera> => {
  const c = await api.get<Cartera>('/api/cartera');
  return { ...c, filas: c.filas.map(conNombreLegible) };
};

/** Cambia la etapa de un cliente (el servidor valida y respeta los efectos de cada etapa). Devuelve su fila ya actualizada. */
export const cambiarEtapa = async (contactId: string, etapa: string): Promise<{ fila: FilaDeCartera }> => {
  const r = await api.post<{ fila: FilaDeCartera }>(`/api/cartera/${contactId}/etapa`, { etapa });
  return { fila: conNombreLegible(r.fila) };
};

/** T14: la salud de una fuente (copiado de `SaludDeFuente` en la API). Los porcentajes son enteros de 0 a 100. */
export interface SaludDeFuente {
  fuente: string;
  total: number;
  /** Cuántos entraron en los últimos 30 días. */
  ultimos30: number;
  contestaron: number;
  avanzaron: number;
  zoom: number;
  cerrados: number;
  perdidos: number;
  /** null: muy pocos datos todavía. */
  nota: 'buena' | 'regular' | 'floja' | null;
  porque: string;
}

/** La salud de cada fuente, de mejor a peor (la calcula el servidor). */
export const listarFuentes = () => api.get<SaludDeFuente[]>('/api/cartera/fuentes');

/** Le pone esa fuente a uno o a varios clientes. */
export const cambiarFuente = (contactIds: string[], fuente: string) => api.post<{ cambiados: number; fuente: string }>('/api/cartera/fuente', { contactIds, fuente });
